import { useFormik } from 'formik';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { UploadField } from '@/components/UploadField';
import { REPORTING_DOCUMENTS } from '@/helpers/reporting';
import { useSubmitFundraisingApplication } from '@/query/queries/fundraising/useSubmitFundraisingApplication';
import { prettyDate } from '@/helpers/date';
import Notify from '@/components/Notification';
import { uploadFile } from '@/store/services/commonServices';
import { VALIDATION_SCHEMA } from './schema';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

export const ReportingForm = ({ report, applicationId }) => {
  const location = useLocation();
  const { t } = useTranslation('reporting');
  const { mutateAsync: submit } = useSubmitFundraisingApplication(applicationId, location.state?.fundraisingId);

  const requiredDocuments = Object.keys(report.reporting.required_documents);

  const checkSameYear = ({ from, to }) => {
    return prettyDate(from, 'YYYY') === prettyDate(to, 'YYYY');
  };

  const uploadFilesOrFail = async files => {
    const uploaded = [];

    for (const file of files) {
      try {
        const result = await uploadFile(file, { protected: true });
        result?.success && uploaded.push(result.data);
      } catch {
        throw new Error(t('reporting_upload_form.upload_file_error', { fileName: file.name }));
      }
    }
    return uploaded;
  };

  const onSubmit = async (values, formik) => {
    const payload = {
      new_funds: Number(location.state?.newFunds),
      refinancings: location.state?.refinancing.map(f => f.fundraising_id),
      documents: {},
    };

    if (values.balance_sheet.length) {
      const result = await uploadFilesOrFail(values.balance_sheet);
      if (result.length !== values.balance_sheet.length)
        return Notify.error({ text: t('reporting_upload_form.upload_error') });
      payload.documents.balance_sheet = result.map(file => file.id);
    }

    if (values.pnl.length) {
      const result = await uploadFilesOrFail(values.pnl);
      if (result.length !== values.pnl.length)
        return Notify.error({ text: t('reporting_upload_form.upload_error') });
      payload.documents.profit_and_loss = result.map(file => file.id);
    }

    if (values.cash_flow.length) {
      const result = await uploadFilesOrFail(values.cash_flow);
      if (result.length !== values.cash_flow.length)
        return Notify.error({ text: t('reporting_upload_form.upload_error') });
      payload.documents.cash_flow = result.map(file => file.id);
    }

    if (values.bank_statement.length) {
      const result = await uploadFilesOrFail(values.bank_statement);
      if (result.length !== values.bank_statement.length)
        return Notify.error({ text: t('reporting_upload_form.upload_error') });
      payload.documents.bank_statement = result.map(file => file.id);
    }

    if (values.loan_agreement.length) {
      const result = await uploadFilesOrFail(values.loan_agreement);
      if (result.length !== values.loan_agreement.length)
        return Notify.error({ text: t('reporting_upload_form.upload_error') });
      payload.documents.loan_agreement = result.map(file => file.id);
    }

    const res = await submit(payload);
    if (res.success) {
      formik.setSubmitting(false);
    }
  };

  const { values, setFieldValue, errors, touched, isSubmitting, handleSubmit } = useFormik({
    validationSchema: VALIDATION_SCHEMA(requiredDocuments),
    initialValues: {
      balance_sheet: [],
      pnl: [],
      cash_flow: [],
      bank_statement: [],
      loan_agreement: [],
    },
    onSubmit,
  });

  return (
    <form className={s.form} onSubmit={handleSubmit}>
      {requiredDocuments.map(doc => {
        const doctype = doc.toString();

        if (doctype === REPORTING_DOCUMENTS.balance_sheet) {
          return (
            <div className={s.card} key={doctype}>
              <div className="f-body f-500">{t('reporting_upload_form.add_balance_sheet')}</div>
              <div className={s.card_content}>
                <div>
                  {t('reporting_upload_form.upload_balance_sheet', {
                    period: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.balance_sheet].to,
                      'MMMM YYYY',
                    ),
                  })}
                </div>
                <UploadField
                  name="balance_sheet"
                  value={values.balance_sheet}
                  onChange={files => setFieldValue('balance_sheet', files)}
                  error={touched.balance_sheet && errors.balance_sheet}
                  dropLabel={t('reporting_upload_form.drop_pdf_csv_xlsx')}
                />
              </div>
            </div>
          );
        }

        if (doctype === REPORTING_DOCUMENTS.pnl) {
          const sameYear = checkSameYear(
            report.reporting.required_documents[REPORTING_DOCUMENTS.pnl],
          );
          return (
            <div className={s.card} key={doctype}>
              <div className="f-body f-500">{t('reporting_upload_form.add_profit_and_loss')}</div>
              <div className={s.card_content}>
                <div>
                  {t('reporting_upload_form.upload_profit_and_loss', {
                    period_from: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.pnl].from,
                      sameYear ? 'MMMM' : 'MMMM YYYY',
                    ),
                    period_to: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.pnl].to,
                      'MMMM',
                    ),
                    year: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.pnl].to,
                      'YYYY',
                    ),
                  })}
                </div>
                <UploadField
                  name="pnl"
                  value={values.pnl}
                  onChange={files => setFieldValue('pnl', files)}
                  error={touched.pnl && errors.pnl}
                  dropLabel={t('reporting_upload_form.drop_pdf_csv_xlsx')}
                />
              </div>
            </div>
          );
        }

        if (doctype === REPORTING_DOCUMENTS.cash_flow) {
          const isSameYear = checkSameYear(
            report.reporting.required_documents[REPORTING_DOCUMENTS.cash_flow],
          );
          return (
            <div className={s.card} key={doctype}>
              <div className="f-body f-500">{t('reporting_upload_form.add_cash_flow')}</div>
              <div className={s.card_content}>
                <div>
                  {t('reporting_upload_form.upload_cash_flow', {
                    period_from: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.cash_flow].from,
                      isSameYear ? 'MMMM' : 'MMMM YYYY',
                    ),
                    period_to: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.cash_flow].to,
                      'MMMM',
                    ),
                    year: prettyDate(
                      report.reporting.required_documents[REPORTING_DOCUMENTS.cash_flow].to,
                      'YYYY',
                    ),
                  })}
                </div>
                <UploadField
                  name="cash_flow"
                  value={values.cash_flow}
                  onChange={files => setFieldValue('cash_flow', files)}
                  error={touched.cash_flow && errors.cash_flow}
                  dropLabel={t('reporting_upload_form.drop_pdf_csv_xlsx')}
                />
              </div>
            </div>
          );
        }

        if (doctype === REPORTING_DOCUMENTS.bank_statement) {
          const isSameYear = checkSameYear(
            report.reporting.required_documents[REPORTING_DOCUMENTS.bank_statement],
          );

          return (
            <div className={s.card} key={doctype}>
              <div className="f-body f-500">{t('reporting_upload_form.add_bank_statements')}</div>
              <div className={s.card_content}>
                <div className={s.card_desc}>
                  <div className="f-body-regular">
                    {t('reporting_upload_form.upload_bank_statements', {
                      period_from: prettyDate(
                        report.reporting.required_documents[REPORTING_DOCUMENTS.bank_statement]
                          .from,
                        isSameYear ? 'MMMM' : 'MMMM YYYY',
                      ),
                      period_to: prettyDate(
                        report.reporting.required_documents[REPORTING_DOCUMENTS.bank_statement].to,
                        'MMMM',
                      ),
                      year: prettyDate(
                        report.reporting.required_documents[REPORTING_DOCUMENTS.bank_statement].to,
                        'YYYY',
                      ),
                    })}
                  </div>
                  <div className="f-body-regular">
                    <Trans
                      ns="reporting"
                      i18nKey="reporting_upload_form.upload_statements_note"
                      components={{
                        Span: <span className="f-700" />,
                      }}
                    />
                  </div>
                </div>
                <UploadField
                  name="bank_statement"
                  value={values.bank_statement}
                  onChange={files => setFieldValue('bank_statement', files)}
                  error={touched.bank_statement && errors.bank_statement}
                  dropLabel={t('reporting_upload_form.drop_pdf_csv_xlsx')}
                />
              </div>
            </div>
          );
        }

        return null;
      })}

      <div className={s.buttons}>
        <button type="submit" className={r.yellow_large} disabled={isSubmitting}>
          {isSubmitting ? t('reporting_upload_form.submitting') : t('reporting_upload_form.submit')}
        </button>
      </div>
    </form>
  );
};

export default ReportingForm;
